// Original implementation from: https://github.com/astoilkov/use-local-storage-state
// Adapted to support session storage and cleaned up a bit
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { hasWindow } from 'utils/browser.utils';

// in memory fallback used then `engine` throws an error
const inMemoryData = new Map<string, unknown>();

type StorageOptions<T> = {
  defaultValue?: T | (() => T);
  storageSync?: boolean;
  serializer?: {
    stringify: (value: unknown) => string;
    parse: (value: string) => unknown;
  };
  engine?: 'localStorage' | 'sessionStorage';
};

// - `useStorageState()` return type
// - first two values are the same as `useState`
type StorageState<T> = [
  T,
  Dispatch<SetStateAction<T>>,
  {
    removeItem: () => void;
  },
];

const callbackMap = new Map<string, Set<(key: string) => void>>();

// notifies all instances using the same `key` to update
function triggerCallbacks(key: string): void {
  for (const callback of callbackMap.get(key) ?? []) {
    callback(key);
  }
}

export function useStorageState(
  key: string,
  options?: StorageOptions<undefined>,
): StorageState<unknown>;
export function useStorageState<T>(
  key: string,
  options?: Omit<StorageOptions<T | undefined>, 'defaultValue'>,
): StorageState<T | undefined>;
export function useStorageState<T>(
  key: string,
  options?: StorageOptions<T>,
): StorageState<T>;
export function useStorageState<T = undefined>(
  key: string,
  options?: StorageOptions<T | undefined>,
): StorageState<T | undefined> {
  const [defaultValue] = useState(options?.defaultValue);
  const engineName = options?.engine ?? 'localStorage';
  const storageEngine = useMemo(() => {
    if (!hasWindow()) return null;
    try {
      return engineName === 'localStorage'
        ? window.localStorage
        : window.sessionStorage;
    } catch (e) {
      return null;
    }
  }, [engineName]);

  // SSR support
  // - on the server, return a constant value
  // - this makes the implementation simpler and smaller because the `engine` object is
  //   `undefined` on the server
  if (!hasWindow()) {
    return [
      defaultValue,
      (): void => {},
      {
        removeItem: (): void => {},
      },
    ];
  }

  const serializer = options?.serializer;
  // disabling ESLint because the above if statement can be executed only on the server. the value
  // of `window` can't change between calls.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useBrowserStorageState(
    key,
    defaultValue,
    options?.storageSync,
    storageEngine!,
    serializer?.parse,
    serializer?.stringify,
  );
}

function useBrowserStorageState<T>(
  key: string,
  defaultValue: T | undefined,
  storageSync: boolean = true,
  engine: Storage,
  parse: (value: string) => unknown = parseJSON,
  stringify: (value: unknown) => string = JSON.stringify,
): StorageState<T | undefined> {
  // store default value in storageEngine:
  // - initial issue: https://github.com/astoilkov/use-local-storage-state/issues/26
  //   issues that were caused by incorrect initial and secondary implementations:
  //   - https://github.com/astoilkov/use-local-storage-state/issues/30
  //   - https://github.com/astoilkov/use-local-storage-state/issues/33
  if (
    !inMemoryData.has(key) &&
    defaultValue !== undefined &&
    goodTry(() => engine.getItem(key)) === null
  ) {
    // reasons for `engine` to throw an error:
    // - maximum quota is exceeded
    // - under Mobile Safari (since iOS 5) when the user enters private mode
    //   `engine.setItem()` will throw
    // - trying to access engine object when cookies are disabled in Safari throws
    //   "SecurityError: The operation is insecure."
    goodTry(() => engine.setItem(key, stringify(defaultValue)));
  }

  // we keep the `parsed` value in a ref because `useSyncExternalStore` requires a cached version
  const storageValue = useRef<{ item: string | null; parsed: T | undefined }>({
    item: null,
    parsed: defaultValue,
  });
  const value = useSyncExternalStore(
    useCallback(
      (onStoreChange) => {
        callbackMap.set(
          key,
          (callbackMap.get(key) ?? new Set()).add(onStoreChange),
        );
        return () => {
          callbackMap.get(key)?.delete(onStoreChange);
          callbackMap.get(key)?.size === 0 && callbackMap.delete(key);
        };
      },
      [key],
    ),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => {
      const item = goodTry(() => engine.getItem(key)) ?? null;
      if (inMemoryData.has(key)) {
        storageValue.current = {
          item,
          parsed: inMemoryData.get(key) as T | undefined,
        };
      } else if (item !== storageValue.current.item) {
        let parsed: T | undefined;

        try {
          parsed = item === null ? defaultValue : (parse(item) as T);
        } catch {
          parsed = defaultValue;
        }

        storageValue.current = {
          item,
          parsed,
        };
      }

      return storageValue.current.parsed;
    },
    () => defaultValue,
  );
  const setState = useCallback(
    (newValue: SetStateAction<T | undefined>): void => {
      const value =
        newValue instanceof Function
          ? newValue(storageValue.current.parsed)
          : newValue;

      // reasons for `engine` to throw an error:
      // - maximum quota is exceeded
      // - under Mobile Safari (since iOS 5) when the user enters private mode
      //   `engine.setItem()` will throw
      // - trying to access `engine` object when cookies are disabled in Safari throws
      //   "SecurityError: The operation is insecure."
      try {
        engine.setItem(key, stringify(value));

        inMemoryData.delete(key);
      } catch {
        inMemoryData.set(key, value);
      }

      triggerCallbacks(key);
    },
    [engine, key, stringify],
  );

  // - syncs change across tabs, windows, iframes
  // - the `storage` event is called only in all tabs, windows, iframe's except the one that
  //   triggered the change
  useEffect(() => {
    if (!storageSync) {
      return undefined;
    }

    const onStorage = (e: StorageEvent): void => {
      if (e.storageArea === goodTry(() => engine) && e.key === key) {
        triggerCallbacks(key);
      }
    };

    window.addEventListener('storage', onStorage);

    return (): void => window.removeEventListener('storage', onStorage);
  }, [engine, key, storageSync]);

  const removeItem = useCallback(() => {
    goodTry(() => engine.removeItem(key));
    inMemoryData.delete(key);
    triggerCallbacks(key);
  }, [engine, key]);

  return useMemo(
    () => [
      value,
      setState,
      {
        removeItem,
      },
    ],
    [value, setState, removeItem],
  );
}

// a wrapper for `JSON.parse()` that supports "undefined" value. otherwise,
// `JSON.parse(JSON.stringify(undefined))` returns the string "undefined" not the value `undefined`
const parseJSON = (value: string): unknown =>
  value === 'undefined' ? undefined : (JSON.parse(value) as unknown);

function goodTry<T>(tryFn: () => T): T | undefined {
  try {
    return tryFn();
  } catch {
    return undefined;
  }
}
